package com.havn.app.features.attachments.domain

import com.havn.app.core.media.HavnPickedFile

const val HAVN_ATTACHMENT_HARD_MAX_BYTES: Long = 100L * 1024L * 1024L

data class HavnAttachment(
    val id: String,
    val storagePath: String,
    val fileName: String,
    val contentType: String? = null,
    val sizeBytes: Long,
    val uploadedByAgentId: String,
    val agencyId: String? = null,
    val propertyId: String? = null,
    val taskId: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null
)

data class AttachmentUploadContext(
    val propertyId: String? = null,
    val taskId: String? = null,
    val agencyId: String? = null
)

data class AgentStorageUsage(
    val usedBytes: Long = 0,
    val limitBytes: Long = 0,
    val remainingBytes: Long = 0,
    val maxFileSizeBytes: Long = 0
)

data class HavnAttachmentSharePhoto(
    val id: String,
    val title: String,
    val url: String,
    val thumbnailDataUrl: String? = null,
    val bucketId: String? = null,
    val storagePath: String? = null,
    val contentType: String? = null
)

data class HavnAttachmentShareLinkTarget(
    val title: String,
    val url: String
)

data class HavnAttachmentRecipient(
    val id: String,
    val displayName: String,
    val email: String? = null,
    val phone: String? = null,
    val source: HavnAttachmentRecipientSource = HavnAttachmentRecipientSource.Contact,
    val lastSentAt: String? = null
) {
    val contactLine: String
        get() = listOfNotNull(phone?.takeIf { it.isNotBlank() }, email?.takeIf { it.isNotBlank() })
            .joinToString(" · ")
            .ifBlank { source.label }
}

enum class HavnAttachmentRecipientSource(val databaseValue: String, val label: String) {
    Contact("contact", "Contact"),
    Agent("agent", "Agent"),
    Recent("recent", "Recent");

    companion object {
        fun fromDatabase(value: String?): HavnAttachmentRecipientSource = when (value) {
            Agent.databaseValue -> Agent
            Recent.databaseValue -> Recent
            else -> Contact
        }
    }
}

interface AttachmentRepository {
    suspend fun allAttachments(): Result<List<HavnAttachment>>
    suspend fun attachmentsForContext(propertyId: String? = null, taskId: String? = null): Result<List<HavnAttachment>>
    suspend fun uploadAttachment(agentId: String, file: HavnPickedFile, context: AttachmentUploadContext): Result<HavnAttachment>
    suspend fun openUrlForAttachment(attachment: HavnAttachment): Result<String>
    suspend fun downloadAttachment(attachment: HavnAttachment): Result<ByteArray>
    suspend fun downloadSharePhoto(photo: HavnAttachmentSharePhoto): Result<ByteArray>
    suspend fun shareUrlsForAttachments(attachments: List<HavnAttachment>): Result<List<Pair<HavnAttachment, String>>>
    suspend fun shortShareUrlsForTargets(
        agentId: String,
        targets: List<HavnAttachmentShareLinkTarget>
    ): Result<List<Pair<HavnAttachmentShareLinkTarget, String>>>
    suspend fun recentShareRecipients(agentId: String): Result<List<HavnAttachmentRecipient>>
    suspend fun recordShareRecipient(agentId: String, recipient: HavnAttachmentRecipient): Result<Unit>
    suspend fun deleteAttachment(agentId: String, attachment: HavnAttachment): Result<Unit>
    suspend fun storageUsage(): Result<AgentStorageUsage>
}
